import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { request } from '@/api/client';
import type { FleetResponse, SiteSummary } from '@/api/types';

export const useFleetStore = defineStore('fleet', () => {
  const sites = ref<SiteSummary[]>([]);
  const summary = ref<FleetResponse['summary']>({
    total: 0, online: 0, warning: 0, alarm: 0, offline: 0,
  });
  const loading = ref(false);
  const error = ref<string | null>(null);
  const loadedAt = ref<number | null>(null);

  const monitoringCentre = computed(() =>
    sites.value.find((s) => s.isMonitoringCentre) ?? null,
  );
  const remoteSites = computed(() =>
    sites.value.filter((s) => !s.isMonitoringCentre),
  );

  async function load(force = false): Promise<void> {
    // The live socket pushes state changes, so a full refetch is only needed
    // on first load or an explicit refresh.
    if (!force && loadedAt.value && Date.now() - loadedAt.value < 15_000) return;

    loading.value = true;
    error.value = null;
    try {
      const res = await request<FleetResponse>('/sites');
      sites.value = res.sites;
      summary.value = res.summary;
      loadedAt.value = Date.now();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'failed to load sites';
    } finally {
      loading.value = false;
    }
  }

  function byId(id: string): SiteSummary | undefined {
    return sites.value.find((s) => s.id === id);
  }

  return { sites, summary, loading, error, monitoringCentre, remoteSites, load, byId };
});
